import { z } from "@/zod";
import { ulid } from "@/schemas/commonFields";
import { projectDependsOn } from "@/schemas/commonFields";
import { dateTime } from "@/schemas/commonFields";

export const PAYROLL_VERSION_BASIC = "basic";
export const PAYROLL_VERSION_CORRECTION = "correction";

export const PAYROLL_STATUS_PREVIEW = "preview";
export const PAYROLL_STATUS_CREATED = "created";
export const PAYROLL_STATUS_SPLIT = "split";
export const PAYROLL_STATUS_CLOSED = "closed";
export const PAYROLL_STATUS_WITHDRAWN = "withdrawn";

export const PAYROLL_STATUS = [
  PAYROLL_STATUS_PREVIEW,
  PAYROLL_STATUS_CREATED,
  PAYROLL_STATUS_SPLIT,
  PAYROLL_STATUS_CLOSED,
  PAYROLL_STATUS_WITHDRAWN,
] as const;

export const PAYROLL_VERSION = [
  PAYROLL_VERSION_BASIC,
  PAYROLL_VERSION_CORRECTION,
] as const;

export type PayrollStatus = (typeof PAYROLL_STATUS)[number];

export const payroll = z.object({
  id: ulid,
  period: z.string().date(),
  payroll_type_id: ulid,
  payroll_type: z.string(),
  project_depends_on: projectDependsOn,
  payroll_version: z.string(),
  payroll_status: z.enum(PAYROLL_STATUS),
  split: z.boolean(),
  created_at: dateTime.nullable(),
  updated_at: dateTime.nullable(),
});

export const readPayroll = payroll.readonly();

export const createPayroll = payroll.omit({
  id: true,
  created_at: true,
  updated_at: true,
});

export const updatePayroll = payroll
  .omit({
    id: true,
    created_at: true,
    updated_at: true,
  })
  .partial();

export const payrollInspectionAssignmentBase = z.object({
  contractor_name: z.string(),
  personal_id_number: z.string(),
  store_name: z.string(),
  contractor_id: ulid,
  end_date: z.string(),
  id: ulid,
  payroll_id: ulid,
  payroll_type_id: ulid,
  period: z.string(),
  start_date: z.string(),
  store_id: z.string(),
});

export const payrollDepartmentBasedInspectionAssignment =
  payrollInspectionAssignmentBase
    .extend({
      department_id: ulid,
      department_name: z.string(),
    })
    .readonly();

export const payrollRoleBasedInspectionAssignment =
  payrollInspectionAssignmentBase
    .extend({
      project_name: z.string(),
      project_role_id: ulid,
      project_role_name: z.string(),
    })
    .readonly();

const payrollInspectionAssignmentBaseForListing =
  payrollInspectionAssignmentBase.omit({
    start_date: true,
    end_date: true,
    period: true,
  });

export const payrollRoleBasedInspectionAssignmentListing =
  payrollInspectionAssignmentBaseForListing
    .extend({
      project_role_id: ulid,
      project_role_name: z.string(),
      project_name: z.string(),
    })
    .readonly();

export const payrollDepartmentBasedInspectionAssignmentListing =
  payrollInspectionAssignmentBaseForListing
    .extend({
      department_id: ulid,
      department_name: z.string(),
    })
    .readonly();

export const createDepartmentBasedPayrollInspection = z.object({
  payroll_id: ulid,
  department_id: ulid,
  store_id: ulid,
  contractor_id: ulid,
});

export const createRoleBasedPayrollInspection = z.object({
  payroll_id: ulid,
  project_role_id: ulid,
  store_id: ulid,
  contractor_id: ulid,
});
